import RsvpApi from '@/lib/api/Rsvp'

const filterByGuestsStatus = (rsvps, status) => {
  const rsvpsFiltered = rsvps.filter((rsvp) => {
    const isRsvpClosed = rsvp.mode === 'closed'
    if (!isRsvpClosed) return true

    const hasGuestsByStatus =
      rsvp.guests.filter((guest) => guest.confirmed === status).length > 0

    return hasGuestsByStatus
  })

  return rsvpsFiltered.map((rsvp) => {
    const isRsvpClosed = rsvp.mode === 'closed'

    return {
      ...rsvp,
      guests: isRsvpClosed
        ? rsvp.guests.filter((guest) => guest.confirmed === status)
        : rsvp.guests,
    }
  })
}

const insertNotInformedGuests = (rsvps) => {
  return rsvps.map((rsvp) => {
    const guestsNotInformed = rsvp.max_guests - rsvp.guests.length

    if (rsvp.mode === 'opened' || guestsNotInformed <= 0) return rsvp

    return {
      ...rsvp,
      guests: [
        ...rsvp.guests,
        ...Array(guestsNotInformed).fill({
          name: 'Não informado',
          confirmed: 'maybe',
        }),
      ],
    }
  })
}

export default {
  state: {
    rsvps: [],
    configs: undefined,
  },

  mutations: {
    SET_RSVPS(state, payload) {
      state.rsvps = payload
    },

    SET_CONFIGS(state, payload) {
      state.configs = payload
    },
  },

  actions: {
    setRsvps(context, payload) {
      let data = payload
      if (data && !Array.isArray(data)) data = Object.values(data)
      context.commit('SET_RSVPS', data)
    },

    setRsvpConfigs(context, payload) {
      context.commit('SET_CONFIGS', payload)
    },

    async fetchRsvpConfigs({ commit, rootGetters }) {
      const token = rootGetters['app/logonToken']
      const api = new RsvpApi(token)

      const configs = await api.getConfigsRsvp()

      if (configs.data.data) {
        commit('SET_CONFIGS', configs.data.data)
      }
    },

    async fetchRsvps({ commit, rootGetters }) {
      const token = rootGetters['app/logonToken']
      const api = new RsvpApi(token)

      const response = await api.getRsvps()
      const rsvpData = response?.data?.data

      if (rsvpData) {
        const rsvps = Array.isArray(rsvpData)
          ? rsvpData
          : Object.values(rsvpData)
        commit('SET_RSVPS', rsvps)
        return rsvps
      }
    },
  },

  getters: {
    getRsvpConfigs(state) {
      if (!state.configs) return undefined

      let configs = state.configs
      const preferencesOpened = state.configs?.preferences?.opened
      const preferencesClosed = state.configs?.preferences?.closed

      configs.preferences.opened.minimum_age_child =
        preferencesOpened?.minimum_age_child || 0

      configs.preferences.opened.limit_adults_qty =
        preferencesOpened?.limit_adults_qty || 1

      configs.preferences.opened.limit_children_qty =
        preferencesOpened?.limit_children_qty || 0

      configs.preferences.opened.require_guest_names =
        preferencesOpened?.require_guest_names ? 1 : 0

      configs.preferences.opened.require_phone =
        !!preferencesOpened?.require_phone

      configs.preferences.opened.show_obs = !!preferencesOpened?.show_obs

      configs.preferences.closed.minimum_age_child =
        preferencesClosed?.minimum_age_child || 0

      configs.preferences.closed.show_obs = !!preferencesClosed?.show_obs

      configs.preferences.closed.show_all_invites =
        !!preferencesClosed?.show_all_invites

      return configs
    },

    getRsvps(state) {
      return state.rsvps
    },

    rsvpListAll(state) {
      const rsvps = insertNotInformedGuests(state.rsvps)
      return rsvps
    },

    rsvpListConfirmed(state) {
      if (state.rsvps.length) {
        const rsvps = state.rsvps.filter(
          (rsvp) => rsvp.confirmed || rsvp.mode === 'closed'
        )
        return filterByGuestsStatus(rsvps, 'yes')
      }
      return []
    },

    rsvpListCancelled(state) {
      if (state.rsvps.length) {
        const rsvps = state.rsvps.filter(
          (rsvp) => !rsvp.confirmed || rsvp.mode === 'closed'
        )
        return filterByGuestsStatus(rsvps, 'no')
      }
      return []
    },

    rsvpListMaybe(state) {
      if (state.rsvps.length) {
        let rsvps = state.rsvps.filter((rsvp) => rsvp.mode === 'closed')
        rsvps = insertNotInformedGuests(rsvps)
        return filterByGuestsStatus(rsvps, 'maybe')
      }
      return []
    },

    rsvpAmountAdultsTotal(state, getters) {
      const rsvps = getters.rsvpListConfirmed

      let adults = 0

      if (rsvps.length > 0) {
        rsvps.forEach((rsvp) => {
          if (rsvp.mode === 'closed') {
            adults += rsvp.guests.filter(
              (guest) => guest.type === 'adult'
            ).length
          } else {
            adults += rsvp.amount_adults
          }
        })
      }

      return adults
    },

    rsvpAmountChildrenTotal(state, getters) {
      const rsvps = getters.rsvpListConfirmed

      let children = 0

      if (rsvps.length > 0) {
        rsvps.forEach((rsvp) => {
          if (rsvp.mode === 'closed') {
            children += rsvp.guests.filter(
              (guest) => guest.type === 'child'
            ).length
          } else {
            children += rsvp.amount_children
          }
        })
      }

      return children
    },

    rsvpAmountAllTotal(state, getters) {
      const rsvps = getters.rsvpListAll

      let all = 0

      if (rsvps.length > 0) {
        rsvps.forEach((rsvp) => {
          if (rsvp.mode === 'closed') {
            all += rsvp.guests.length
          } else {
            all += rsvp.amount_children + rsvp.amount_adults
          }
        })
      }
      return all
    },

    rsvpAmountConfirmedTotal(state, getters) {
      const rsvps = getters.rsvpListConfirmed

      let all = 0

      if (rsvps.length > 0) {
        rsvps.forEach((rsvp) => {
          if (rsvp.mode === 'closed') {
            all += rsvp.guests.length
          } else {
            all += rsvp.amount_children + rsvp.amount_adults
          }
        })
      }
      return all
    },

    rsvpAmountCancelledTotal(state, getters) {
      let cancelled = 0
      if (getters.rsvpListCancelled.length) {
        getters.rsvpListCancelled.forEach((rsvp) => {
          cancelled += rsvp.guests.length
        })
      }
      return cancelled
    },

    rsvpAmountMaybeTotal(state, getters) {
      let cancelled = 0
      if (getters.rsvpListMaybe.length) {
        getters.rsvpListMaybe.forEach((rsvp) => {
          cancelled += rsvp.guests.length
        })
      }
      return cancelled
    },
  },
}
